import moment from 'moment';
import { useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import "tailwindcss/tailwind.css";

const localizer = momentLocalizer(moment);

const initialEvents = [
  {
    id: 0,
    title: 'Booked Slot',
    start: new Date(2024, 9, 18, 16, 30), // 4:30 PM
    end: new Date(2024, 9, 18, 18, 30),   // 6:30 PM
  },
];

const SelectSlot = () => {
  const [myEvents, setMyEvents] = useState(initialEvents);
  const [selectedDate, setSelectedDate] = useState(new Date(2024, 9, 18)); // Initial date
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogDetails, setDialogDetails] = useState({ start: null, end: null, title: '' });
  const [selectedSlot, setSelectedSlot] = useState(null); // To track the selected slot

  const handleSelectSlot = ({ start, end }) => {
    setSelectedSlot({ start, end }); // Highlight the selected slot
    setDialogDetails({ start, end, title: '' });
    setOpenDialog(true);
  };

  const handleSaveEvent = () => {
    if (dialogDetails.title.trim()) {
      setMyEvents((prev) => [...prev, { id: prev.length, ...dialogDetails }]);
    }
    setSelectedSlot(null); // Clear the selected slot after saving
    setOpenDialog(false);
  };

  const handleDialogClose = () => {
    setSelectedSlot(null); // Clear the selected slot after closing
    setOpenDialog(false);
  };

  const handleEventResize = ({ event, start, end }) => {
    const nextEvents = myEvents.map((existingEvent) =>
      existingEvent.id === event.id
        ? { ...existingEvent, start, end }
        : existingEvent
    );
    setMyEvents(nextEvents);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 to-gray-100 p-6">
      <div className="flex-grow flex flex-col">
        <h1 className="text-4xl font-extrabold text-blue-800 mb-8 text-center">Flex Office Slot Booking</h1>

        {/* Date Picker */}
        <div className="mb-6 flex flex-col items-center">
          <label className="block text-lg font-bold text-gray-700 mb-2" htmlFor="date">
            Select Date:
          </label>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            className="p-3 border rounded-lg w-64 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
            dateFormat="MMMM d, yyyy"
            todayButton="Today"
            popperPlacement="bottom"
          />
        </div>

        {/* Calendar */}
        <div className="flex-grow mb-8 rounded-lg bg-white shadow-lg p-4">
          <Calendar
            selectable
            localizer={localizer}
            events={myEvents}
            defaultView="day"
            views={['day', 'week']}
            defaultDate={selectedDate}
            onSelectSlot={handleSelectSlot}
            onEventResize={handleEventResize}
            resizable
            style={{ height: '100%', width: '100%' }}
            className="rounded-lg"
            slotPropGetter={(slot) => {
              if (selectedSlot && slot.start >= selectedSlot.start && slot.end <= selectedSlot.end) {
                return { style: { backgroundColor: 'var(--flex-primary-soft, #b3e5fc)', border: '1px solid var(--flex-primary, #2196f3)' } }; // Highlight selected slot
              }
              return {};
            }}
          />
        </div>

        {/* Event List */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">Scheduled Events:</h2>
          <ul className="bg-gray-50 p-4 rounded-lg shadow-lg">
            {myEvents.length > 0 ? (
              myEvents.map((event) => (
                <li
                  key={event.id}
                  className="border-b last:border-b-0 py-3 flex justify-between items-center">
                  <div>
                    <span className="font-semibold text-gray-700">{event.title}</span>
                    <div className="text-sm text-gray-500">
                      {moment(event.start).format('MMMM D, YYYY h:mm A')} - {moment(event.end).format('h:mm A')}
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <p className="text-gray-500 text-center">No events scheduled.</p>
            )}
          </ul>
        </div>
      </div>

      {/* Dialog for adding events */}
      {openDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
            {/* Close Button (X) */}
            <button
              onClick={handleDialogClose}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-xl font-bold text-gray-800 mb-4">Add Booking</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Selected Date:
              </label>
              <p className="text-gray-600">
                {moment(dialogDetails.start).format('MMMM D, YYYY')}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Selected Time:
              </label>
              <p className="text-gray-600">
                {moment(dialogDetails.start).format('h:mm A')} - {moment(dialogDetails.end).format('h:mm A')}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Purpose of Booking:
              </label>
              <input
                type="text"
                value={dialogDetails.title}
                onChange={(e) => setDialogDetails({ ...dialogDetails, title: e.target.value })}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              //placeholder="Enter purpose"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleDialogClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEvent}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectSlot;
